import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Field,
  ModalActions,
  TextArea,
  TimeField,
  readAttachmentContent,
} from '@evogard/ui';
import {
  businessDate,
  houseLocalOf,
  instantFromHouseLocal,
  type BreakStatus,
  type Tone,
} from '@evogard/domain';
import type { BreakRecord } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { BREAK_CURRENT_KEY } from '../../components/BreakToggle.js';
import { BREAK_OVERRUNS_KEY } from '../../components/BreakOverrunsBadge.js';

/** A raiz das consultas do módulo — invalidar ela relê relação e gráfico. */
export const BREAKS_KEY = ['intervalo'] as const;

// @valor
export const STATUS_LABEL: Record<BreakStatus, string> = {
  pontual: 'Pontual',
  em_andamento: 'Em andamento',
  estourado: 'Estourado',
};

// @valor
export const STATUS_TONE: Record<BreakStatus, Tone> = {
  pontual: 'sucesso',
  em_andamento: 'progresso',
  estourado: 'erro',
};

/** `HH:MM` no relógio da casa. */
export function clockOf(iso: string): string {
  return houseLocalOf(iso).slice(11);
}

/** O período de N dias que termina hoje. */
export function periodOf(days: number): { from: string; to: string } {
  const now = Date.now();
  return {
    from: businessDate(new Date(now - (days - 1) * 86_400_000)),
    to: businessDate(new Date(now)),
  };
}

/** O recorte da relação. `departmentId` 0 é "sem departamento". */
export interface BreakFilter {
  days: number;
  departmentId?: number;
}

export function useBreakRecords(filter: BreakFilter, page: number, perPage: number) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: [...BREAKS_KEY, 'relacao', tenantId, filter, page, perPage],
    enabled: tenantId !== undefined,
    placeholderData: (previous) => previous,
    queryFn: async () =>
      ok(
        await api.breaks.records({
          query: {
            ...periodOf(filter.days),
            departmentId: filter.departmentId,
            page,
            perPage,
          },
        }),
        200,
        'Não foi possível carregar a relação.',
      ),
  });
}

export function useBreakHistory(filter: BreakFilter) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: [...BREAKS_KEY, 'historico', tenantId, filter],
    enabled: tenantId !== undefined,
    queryFn: async () =>
      ok(
        await api.breaks.history({
          query: { days: filter.days, departmentId: filter.departmentId },
        }),
        200,
        'Não foi possível carregar o gráfico.',
      ),
  });
}

/** O período por departamento — só para quem vê a base inteira. */
export function useBreakDepartments(days: number, enabled: boolean) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: [...BREAKS_KEY, 'departamentos', tenantId, days],
    enabled: enabled && tenantId !== undefined,
    queryFn: async () =>
      ok(
        await api.breaks.departmentsReport({ query: { days } }),
        200,
        'Não foi possível carregar o relatório por departamento.',
      ),
  });
}

/** Relê tudo que um ajuste muda: relação, gráfico, botão do topo e estouros. */
export function useRefreshBreaks() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: BREAKS_KEY });
    void client.invalidateQueries({ queryKey: BREAK_CURRENT_KEY });
    void client.invalidateQueries({ queryKey: BREAK_OVERRUNS_KEY });
  };
}

/**
 * O formulário de ajuste — o mesmo para pedir (o próprio) e para ajustar
 * direto (quem tem `breaks.records:ajustar`). Justificativa e comprovante são
 * obrigatórios nos dois, como no legado.
 */
export function AdjustmentForm({
  record,
  mode,
  onDone,
}: {
  record: BreakRecord;
  mode: 'pedir' | 'ajustar';
  onDone: () => void;
}) {
  const refresh = useRefreshBreaks();
  const [start, setStart] = useState(clockOf(record.startedAt));
  const [end, setEnd] = useState(clockOf(record.endedAt ?? record.expectedEndAt));
  const [justification, setJustification] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const startedAt = instantFromHouseLocal(`${record.day}T${start}`);
      const endedAt = instantFromHouseLocal(`${record.day}T${end}`);
      if (!startedAt || !endedAt) throw new Error('Informe os dois horários.');
      if (!file) throw new Error('Anexe o comprovante.');
      const body = {
        startedAt: startedAt.toISOString(),
        endedAt: endedAt.toISOString(),
        justification,
        file: {
          nome: file.name,
          typeMime: file.type || 'application/octet-stream',
          content: await readAttachmentContent(file),
        },
      };
      return mode === 'pedir'
        ? ok(
            await api.breaks.requestAdjustment({ params: { id: record.id }, body }),
            201,
            'Não foi possível enviar a solicitação.',
          )
        : ok(
            await api.breaks.adjust({ params: { id: record.id }, body }),
            200,
            'Não foi possível ajustar.',
          );
    },
    onSuccess: () => {
      refresh();
      onDone();
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <TimeField label="Início" required layer="modal" value={start} onChange={setStart} />
        <TimeField label="Fim" required layer="modal" value={end} onChange={setEnd} />
      </div>
      <TextArea
        label="Justificativa"
        required
        maxLength={2000}
        value={justification}
        onChange={(e) => setJustification(e.target.value)}
      />
      <Field
        label="Comprovante"
        type="file"
        required
        accept="image/*,application/pdf"
        help="Obrigatório. Imagem ou PDF."
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      {save.isError && <Alert tone="erro">{save.error.message}</Alert>}
      <ModalActions>
        <Button type="submit" loading={save.isPending}>
          {mode === 'pedir' ? 'Enviar solicitação' : 'Ajustar'}
        </Button>
      </ModalActions>
    </form>
  );
}

/** Aprovar aplica os horários pedidos; reprovar só responde. */
export function ReviewForm({ record, onDone }: { record: BreakRecord; onDone: () => void }) {
  const refresh = useRefreshBreaks();
  const [note, setNote] = useState('');
  const adjustment = record.adjustment!;
  const review = useMutation({
    mutationFn: async (decision: 'aprovado' | 'reprovado') =>
      ok(
        await api.breaks.reviewAdjustment({
          params: { id: adjustment.id },
          body: { decision, note: note || undefined },
        }),
        200,
        'Não foi possível revisar.',
      ),
    onSuccess: () => {
      refresh();
      onDone();
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-texto-suave">
        De {clockOf(adjustment.previousStartedAt)}–
        {adjustment.previousEndedAt ? clockOf(adjustment.previousEndedAt) : 'aberto'} para{' '}
        <strong className="text-texto">
          {clockOf(adjustment.requestedStartedAt)}–{clockOf(adjustment.requestedEndedAt)}
        </strong>
        .
      </p>
      <p className="text-sm">{adjustment.justification}</p>
      <TextArea
        label="Observação (opcional)"
        maxLength={2000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {review.isError && <Alert tone="erro">{review.error.message}</Alert>}
      <ModalActions>
        <Button
          variant="perigo"
          loading={review.isPending && review.variables === 'reprovado'}
          onClick={() => review.mutate('reprovado')}
        >
          Reprovar
        </Button>
        <Button
          loading={review.isPending && review.variables === 'aprovado'}
          onClick={() => review.mutate('aprovado')}
        >
          Aprovar
        </Button>
      </ModalActions>
    </div>
  );
}

/** Abre o comprovante numa aba nova, pela URL assinada. */
export async function openFile(fileId: number): Promise<void> {
  const r = ok(
    await api.breaks.fileUrl({ params: { id: fileId } }),
    200,
    'Não foi possível abrir o comprovante.',
  );
  window.open(r.url, '_blank', 'noopener');
}
